/**
 * StaffSala — Sala Live (Fase 1 · touch-first, tablet-first).
 *
 * Legge dinamicamente le sale attive da /api/staff/rooms.
 * Renderizza TAB orizzontali scrollabili (touch swipe naturale, no
 * dropdown, no frecce indispensabili).
 * Una sola sala visibile alla volta.
 * Memorizza l'ultima sala in localStorage; fallback automatico alla
 * prima sala per display_order se quella salvata è archiviata/rimossa.
 *
 * Fase 1: KPI operativi = "0 tavoli configurati" (nessun dato inventato).
 * I tavoli arriveranno in Fase 2.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Users, LayoutGrid } from "lucide-react";

const LS_KEY = "sarabi.staff.sala.lastRoomSlug";


function KpiCard({ label, value, sublabel, testId, Icon }) {
  return (
    <div className="rounded-2xl border-2 border-sand-300 bg-white p-4 sm:p-5"
         data-testid={testId}>
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-ink-400" strokeWidth={1.75} />}
        <div className="eyebrow">{label}</div>
      </div>
      <div className="mt-2 font-sans font-semibold text-3xl sm:text-4xl tabular-nums text-ink-900">
        {value}
      </div>
      {sublabel && (
        <div className="text-xs text-ink-500 mt-1 font-sans">{sublabel}</div>
      )}
    </div>
  );
}


export default function StaffSala() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSlug, setActiveSlug] = useState(null);
  const tabsRef = useRef(null);

  // Fetch rooms
  useEffect(() => {
    api.get("/staff/rooms")
      .then((r) => setRooms(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  // Compute default active slug once rooms are loaded (with LS fallback)
  useEffect(() => {
    if (loading || rooms.length === 0) return;
    // Se activeSlug è già valido (e appartiene alle sale caricate), lascia stare
    if (activeSlug && rooms.some((r) => r.slug === activeSlug)) return;
    const saved = localStorage.getItem(LS_KEY);
    const savedIsValid = saved && rooms.some((r) => r.slug === saved);
    const fallback = rooms[0].slug; // rooms già ordinate per display_order
    setActiveSlug(savedIsValid ? saved : fallback);
  }, [loading, rooms, activeSlug]);

  const activeRoom = useMemo(
    () => rooms.find((r) => r.slug === activeSlug) || null,
    [rooms, activeSlug],
  );

  const tablesCount = activeRoom?.tables_count ?? 0;

  const selectRoom = (slug) => {
    setActiveSlug(slug);
    try { localStorage.setItem(LS_KEY, slug); } catch { /* noop */ }
    // scroll active tab into view
    requestAnimationFrame(() => {
      const el = tabsRef.current?.querySelector(`[data-slug="${slug}"]`);
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
      }
    });
  };

  return (
    <div data-testid="staff-sala-page">
      <div className="mb-4 sm:mb-6">
        <div className="eyebrow">Servizio</div>
        <h1 className="font-serif text-3xl sm:text-4xl mt-1">Sala Live</h1>
        <p className="text-sm text-ink-500 mt-1 font-sans max-w-xl">
          Panoramica per sala. I tavoli saranno configurabili nella prossima release.
        </p>
      </div>

      {loading ? (
        <div className="text-ink-400 py-10 font-sans">Caricamento sale…</div>
      ) : rooms.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-sand-300 p-10 text-center text-ink-500 font-sans"
             data-testid="staff-sala-no-rooms">
          Nessuna sala attiva. L'Admin può configurarle da Impostazioni → Sale.
        </div>
      ) : (
        <>
          {/* TAB orizzontali scrollabili — touch-first, no arrows required */}
          <div ref={tabsRef}
               role="tablist"
               aria-label="Sale"
               data-testid="staff-sala-tabs"
               className="-mx-4 px-4 flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory
                          scrollbar-thin scrollbar-thumb-sand-300"
               style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-x" }}>
            {rooms.map((r) => {
              const active = r.slug === activeSlug;
              return (
                <button
                  key={r.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  data-slug={r.slug}
                  data-testid={`staff-sala-tab-${r.slug}`}
                  onClick={() => selectRoom(r.slug)}
                  className={`shrink-0 snap-start min-h-[44px] px-5 sm:px-6 py-2.5 rounded-full
                              font-sans font-medium text-sm sm:text-base tracking-tight
                              transition-colors whitespace-nowrap
                              ${active
                                ? "bg-bronze-600 text-white shadow-sm"
                                : "bg-white text-ink-700 border-2 border-sand-300 active:bg-sand-100"}`}
                >
                  {r.name}
                </button>
              );
            })}
          </div>

          {/* Contenuto sala attiva */}
          {activeRoom && (
            <div className="mt-6" data-testid={`staff-sala-content-${activeRoom.slug}`}>
              <div className="mb-4 flex items-baseline gap-2">
                <MapPin className="w-4 h-4 text-ink-400" strokeWidth={1.75} />
                <span className="font-sans font-semibold text-lg text-ink-900">
                  {activeRoom.name}
                </span>
              </div>

              {/* KPI operativi Fase 2 · tables_count dal backend */}
              <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                <KpiCard testId="staff-sala-kpi-tables"
                         Icon={LayoutGrid}
                         label="Tavoli configurati" value={tablesCount}
                         sublabel={tablesCount === 0 ? "Configura dall'anagrafica sale" : "—"} />
                <KpiCard testId="staff-sala-kpi-busy"
                         Icon={LayoutGrid}
                         label="Tavoli occupati" value="0" sublabel="—" />
                <KpiCard testId="staff-sala-kpi-covers"
                         Icon={Users}
                         label="Coperti" value="0" sublabel="—" />
                <KpiCard testId="staff-sala-kpi-availability"
                         Icon={MapPin}
                         label="Disponibilità" value="—" sublabel="—" />
              </div>

              <div className="mt-6 rounded-2xl border border-dashed border-sand-300 p-8 sm:p-10 text-center text-ink-500 font-sans"
                   data-testid="staff-sala-tables-placeholder">
                <LayoutGrid className="w-8 h-8 mx-auto text-ink-400 mb-2" strokeWidth={1.5} />
                {tablesCount === 0
                  ? <>0 tavoli configurati in <span className="font-medium text-ink-700">{activeRoom.name}</span>.</>
                  : <><span className="tabular-nums font-medium text-ink-700">{tablesCount}</span> tavoli configurati in <span className="font-medium text-ink-700">{activeRoom.name}</span>. La mappa arriverà in una prossima release.</>}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
